import { FileDrop } from "@rosc/ui/v32/file-drop";
import { Notice } from "@rosc/ui/v32/notice";
import {
  ChevronRight,
  CircleCheck,
  FileClock,
  FileText,
  Loader2,
  Sparkles,
} from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import type {
  CompletedFormSummary,
  FormTemplate,
} from "@/lib/form-completion/queries";
import { cn } from "@/lib/utils";

export type FormProblem = {
  title: string;
  description?: string;
};

const ROW =
  "flex min-h-14 w-full items-center gap-3 rounded-[14px] bg-v3-surface-2 px-3.5 py-3 text-left shadow-v3-hairline transition-colors hover:bg-v3-surface-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand disabled:pointer-events-none disabled:opacity-50";
const TILE =
  "flex size-9 shrink-0 items-center justify-center rounded-xl bg-v3-panel text-v3-text-2 shadow-v3-hairline [&_svg]:size-[18px]";

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="mb-2 text-[13px] font-semibold text-v3-text-2">{children}</h3>
);

/**
 * The first step: the visit's filled forms, the clinician's saved forms,
 * forms still to set up, the starter forms and the upload area.
 */
export const FormChooser = ({
  templates,
  starters,
  recent,
  loading,
  busy,
  problem,
  onPick,
  onSetUp,
  onStarter,
  onUpload,
  onOpenRecent,
}: {
  templates: FormTemplate[];
  starters: { key: string; name: string }[];
  recent: CompletedFormSummary[];
  loading: boolean;
  // A form is being read or a starter copied.
  busy: boolean;
  problem: FormProblem | null;
  onPick: (template: FormTemplate) => void;
  onSetUp: (template: FormTemplate) => void;
  onStarter: (key: string) => void;
  onUpload: (file: File) => void;
  onOpenRecent: (form: CompletedFormSummary) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const ready = templates.filter((item) => item.state === "ready");
  const drafts = templates.filter((item) => item.state === "draft");
  // A starter already copied into the account is not offered again.
  const used = new Set(
    templates
      .filter((item) => item.source === "starter")
      .map((item) => item.name),
  );
  const offered = starters.filter((starter) => !used.has(starter.name));
  return (
    <div className="space-y-6 px-5 pb-6 pt-1" data-testid="form-chooser">
      <div>
        <h2 className="font-v3-display text-[24px] font-bold leading-8 tracking-[-0.01em]">
          {t("forms.choose.heading")}
        </h2>
        <p className="mt-1.5 text-[15px] leading-[22px] text-v3-text-2">
          {t("forms.choose.description")}
        </p>
      </div>
      {problem && (
        <Notice
          description={problem.description}
          testId="form-problem"
          title={problem.title}
          tone="error"
        />
      )}
      {recent.length > 0 && (
        <section>
          <SectionTitle>{t("forms.choose.recent")}</SectionTitle>
          <ul className="space-y-2">
            {recent.map((form) => (
              <li key={form.public_id}>
                <button
                  className={ROW}
                  data-testid="form-recent"
                  type="button"
                  onClick={() => onOpenRecent(form)}
                >
                  <span className={TILE}>
                    {form.state === "completed" ? (
                      <CircleCheck className="text-v3-ok-text" />
                    ) : (
                      <FileClock />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold">
                      {form.name}
                    </span>
                    <span className="block text-[13px] text-v3-text-3">
                      {form.state === "completed"
                        ? t("forms.choose.completed")
                        : t("forms.choose.inReview")}
                    </span>
                  </span>
                  <ChevronRight aria-hidden className="size-4 text-v3-text-3" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <SectionTitle>{t("forms.choose.saved")}</SectionTitle>
        {loading ? (
          <p className="flex items-center gap-2 text-[14px] text-v3-text-3">
            <Loader2 aria-hidden className="size-4 animate-spin" />
            {t("common.loading")}
          </p>
        ) : ready.length === 0 && drafts.length === 0 ? (
          <p className="rounded-[14px] bg-v3-surface-2 px-4 py-3 text-[14px] leading-5 text-v3-text-3">
            {t("forms.choose.empty")}
          </p>
        ) : (
          <ul className="space-y-2">
            {ready.map((template) => (
              <li key={template.public_id}>
                <button
                  className={ROW}
                  data-testid="form-template"
                  disabled={busy}
                  type="button"
                  onClick={() => onPick(template)}
                >
                  <span className={TILE}>
                    <FileText />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold">
                      {template.name}
                    </span>
                    <span className="block text-[13px] text-v3-text-3">
                      {t("forms.fields.count", {
                        count: template.fields.length,
                      })}
                    </span>
                  </span>
                  <ChevronRight aria-hidden className="size-4 text-v3-text-3" />
                </button>
              </li>
            ))}
            {drafts.map((template) => (
              <li key={template.public_id}>
                <button
                  className={cn(ROW, "bg-v3-warn-tint")}
                  data-testid="form-draft"
                  disabled={busy}
                  type="button"
                  onClick={() => onSetUp(template)}
                >
                  <span className={TILE}>
                    <FileClock />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold">
                      {template.name}
                    </span>
                    <span className="block text-[13px] text-v3-warn-text">
                      {t("forms.choose.setup")}
                    </span>
                  </span>
                  <ChevronRight aria-hidden className="size-4 text-v3-text-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      {offered.length > 0 && (
        <section>
          <SectionTitle>{t("forms.choose.starters")}</SectionTitle>
          <ul className="space-y-2">
            {offered.map((starter) => (
              <li key={starter.key}>
                <button
                  className={ROW}
                  data-testid="form-starter"
                  disabled={busy}
                  type="button"
                  onClick={() => onStarter(starter.key)}
                >
                  <span className={TILE}>
                    <Sparkles />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-semibold leading-5">
                      {t(`forms.starters.${starter.key}`, {
                        defaultValue: starter.name,
                      })}
                    </span>
                    <span className="block text-[13px] text-v3-text-3">
                      {t("forms.choose.starterHint")}
                    </span>
                  </span>
                  <ChevronRight aria-hidden className="size-4 text-v3-text-3" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <FileDrop
        accept="application/pdf,.pdf"
        busy={busy}
        hint={busy ? undefined : t("forms.choose.uploadHint")}
        icon={busy ? <Loader2 className="animate-spin" /> : undefined}
        testId="form-upload"
        title={busy ? t("forms.choose.reading") : t("forms.choose.upload")}
        onFile={onUpload}
      />
    </div>
  );
};
